import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { requireEnv } from "@/lib/requireEnv";

/**
 * The gate.
 *
 * Four outcomes, and each says something different:
 *
 *  - **Still asking** → nothing, rather than a flash of the login screen.
 *  - **Not signed in** → the login page.
 *  - **Not an admin** → a page saying so, and a link to the back office.
 *    Somebody who administers an institution and has followed the wrong
 *    bookmark should be sent somewhere useful rather than told "denied".
 *  - **No second factor** → enrolment, from anywhere, with no way round it.
 *
 * This is convenience. The security rules and every callable re-check the role
 * independently, so somebody who edits their way past it reaches screens that
 * show them nothing.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const t = useT();

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-muted">
        {t("common.loading")}
      </div>
    );
  }

  if (status === "signedOut") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (status === "notAdmin") {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <h1 className="font-display text-2xl text-ink">{t("error.notAdmin.title")}</h1>
        <p className="mt-3 text-ink-muted">{t("error.notAdmin.body")}</p>
        <a
          href={requireEnv("VITE_BACKOFFICE_BASE_URL")}
          className="mt-6 inline-block text-sm text-cue-green underline underline-offset-2"
        >
          {t("error.notAdmin.toBackOffice")}
        </a>
      </div>
    );
  }

  if (status === "needsSecondFactor" && location.pathname !== "/enrol") {
    return <Navigate to="/enrol" replace />;
  }

  return <>{children}</>;
}
